import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, TrendingDown, ArrowUpRight, FileText, BarChart3, HelpCircle } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { statusClass } from "@/lib/status";
import { WatchButton } from "@/components/WatchButton";
import { CompanyMonogram } from "@/components/CompanyMonogram";
import { BuyShares } from "@/components/BuyShares";
import { DealCountdown } from "@/components/DealCountdown";

const fmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-[#6D28D9]/30 bg-[#F6F4FD] px-4 py-3">
      <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className="font-display mt-1 text-lg font-semibold text-[#6D28D9]">₹{fmt.format(payload[0].value)}</p>
    </div>
  );
};

export default function CompanyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/opportunities/${slug}`)
      .then((res) => setCompany(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const enquire = () => {
    sessionStorage.setItem("enquire_share", company.name);
    navigate("/");
  };

  if (notFound) {
    return (
      <div data-testid="company-not-found" className="flex min-h-screen flex-col items-center justify-center bg-[#FFFFFF] text-slate-900">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">Company not found</p>
        <Link to="/" className="mt-6 border border-[#6D28D9]/40 px-6 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#6D28D9]">
          Back to the board
        </Link>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FFFFFF]">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">Loading research…</p>
      </div>
    );
  }

  const hasChange = typeof company.change_pct === "number";
  const up = hasChange && company.change_pct >= 0;

  return (
    <div data-testid="company-detail-page" className="min-h-screen bg-[#FFFFFF] text-slate-900">
      <header className="border-b border-slate-200 bg-[#FFFFFF]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" data-testid="company-back-button" className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.22em] text-slate-500 transition-colors hover:text-[#6D28D9]">
            <ArrowLeft className="h-4 w-4" /> The Board
          </Link>
          <span className="flex items-center gap-3 font-display text-lg font-semibold tracking-[0.18em]">
            <img src="/logo.png" alt="PRE-IPO EXPERT" className="h-10 w-10 rounded-full object-contain" />
            <span className="hidden whitespace-nowrap sm:inline">PRE-IPO <span className="text-[#6D28D9]">EXPERT</span></span>
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="flex items-start gap-5">
            <CompanyMonogram name={company.name} logo={company.logo_path} size="lg" testid="company-logo" />
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#6D28D9]/90">{company.sector}</p>
                <span data-testid="company-status" className={`border px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-[0.18em] ${statusClass(company.status)}`}>
                  {company.status}
                </span>
                {company.closes_at && <DealCountdown closesAt={company.closes_at} size="lg" testid="company-deal-countdown" />}
              </div>
              <h1 data-testid="company-name" className="font-display mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">{company.name}</h1>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-500">{company.about}</p>

          <div className="mt-10 grid grid-cols-2 gap-px border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-5">
            {[
              { label: "Indicative Price", value: `₹${fmt.format(company.price)}`, testid: "company-price" },
              { label: "1Y Change", value: hasChange ? `${up ? "+" : ""}${company.change_pct}%` : "n/a", testid: "company-change", cls: !hasChange ? "text-slate-400" : up ? "text-emerald-600" : "text-rose-600" },
              { label: "Market Lot", value: company.lot_size, testid: "company-lot" },
              { label: "Lot Value", value: `₹${fmt.format(Math.round(company.price * company.lot_size))}`, testid: "company-lot-value" },
              { label: "Market Cap", value: company.market_cap || "Not disclosed", testid: "company-market-cap", cls: company.market_cap ? "" : "text-slate-400 !text-base sm:!text-lg" },
            ].map((s) => (
              <div key={s.label} className="bg-[#F6F4FD] px-5 py-5 last:col-span-2 sm:px-6 sm:py-6 sm:last:col-span-1">
                <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
                <p data-testid={s.testid} className={`font-display mt-2 break-words text-xl font-semibold sm:text-2xl ${s.cls || "text-slate-900"}`}>{s.value}</p>
              </div>
            ))}
          </div>
          <div id="buy" className="mt-6"><BuyShares company={company} /></div>
        </motion.div>

        <motion.section
          data-testid="company-price-chart"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-16 border border-slate-200 bg-[#F6F4FD]/70 p-8"
        >
          <div className="flex items-center gap-3">
            <BarChart3 className="h-4 w-4 text-[#6D28D9]" />
            <h2 className="font-display text-xl font-semibold tracking-tight">Price History — 18 Months</h2>
          </div>
          <p className="mt-2 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500">Indicative unlisted-market prices · illustrative sample data</p>
          <div className="mt-8 h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={company.price_history} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="d" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} interval={2} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={70} domain={["auto", "auto"]} tickFormatter={(v) => `₹${fmt.format(v)}`} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(109, 40, 217,0.3)" }} />
                <Line type="monotone" dataKey="p" stroke="#6D28D9" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: "#6D28D9", stroke: "#FFFFFF" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.section>

        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <motion.section
            data-testid="company-research-notes"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-[#6D28D9]" />
              <h2 className="font-display text-xl font-semibold tracking-tight">Desk Research Notes</h2>
            </div>
            <div className="mt-6 space-y-6">
              {(company.research || []).map((para, i) => (
                <div key={para.slice(0, 40)} className="grid grid-cols-[auto_1fr] gap-5 border-b border-slate-200 pb-6 last:border-0">
                  <span className="font-display text-2xl font-semibold text-[#6D28D9]/30">0{i + 1}</span>
                  <p className="text-sm leading-relaxed text-slate-700 sm:text-base">{para}</p>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section
            data-testid="company-financials-table"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3">
              {up ? <TrendingUp className="h-4 w-4 text-[#6D28D9]" /> : <TrendingDown className="h-4 w-4 text-rose-600" />}
              <h2 className="font-display text-xl font-semibold tracking-tight">Key Financials</h2>
            </div>
            {company.financials && company.financials.length > 0 ? (
              <>
                <table className="mt-6 w-full border border-slate-200 text-sm">
                  <thead>
                    <tr className="bg-[#F6F4FD] font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      <th className="px-4 py-3 text-left">Metric</th>
                      <th className="px-4 py-3 text-right">FY24</th>
                      <th className="px-4 py-3 text-right">FY25</th>
                    </tr>
                  </thead>
                  <tbody>
                    {company.financials.map((f) => (
                      <tr key={f.metric} className="border-t border-slate-200">
                        <td className="px-4 py-3.5 text-slate-500">{f.metric}</td>
                        <td className="px-4 py-3.5 text-right text-slate-700">{f.fy24}</td>
                        <td className="px-4 py-3.5 text-right font-medium text-[#6D28D9]">{f.fy25}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-3 font-mono2 text-[9px] uppercase tracking-[0.18em] text-slate-400">Illustrative figures · verify with latest filings</p>
              </>
            ) : (
              <div className="mt-6 border border-dashed border-slate-300 p-8 text-center">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">Detailed financials being compiled</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">Request the latest verified figures through an enquiry — the desk will share filings directly.</p>
              </div>
            )}
          </motion.section>
        </div>

        <motion.section
          data-testid="company-faq-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-16"
        >
          <div className="flex items-center gap-3">
            <HelpCircle className="h-4 w-4 text-[#6D28D9]" />
            <h2 className="font-display text-xl font-semibold tracking-tight">Frequently Asked</h2>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {(company.faqs || []).map((f, i) => (
              <div key={f.q} data-testid={`company-faq-${i}`} className="card-glow border border-slate-200 bg-[#F6F4FD]/70 p-6">
                <h3 className="font-display text-base font-medium leading-snug text-slate-900">{f.q}</h3>
                <p className="mt-3 text-xs leading-relaxed text-slate-500">{f.a}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 flex flex-col items-start justify-between gap-6 border border-[#6D28D9]/25 bg-[#6D28D9]/[0.05] p-8 sm:flex-row sm:items-center"
        >
          <div>
            <h3 className="font-display text-xl font-semibold tracking-tight">Interested in {company.name}?</h3>
            <p className="mt-2 text-sm text-slate-500">Request a current desk quote — availability and pricing confirmed within one business day.</p>
          </div>
          <button
            data-testid="company-enquire-button"
            onClick={enquire}
            className="group flex shrink-0 items-center gap-3 bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-transform duration-300 hover:scale-[1.03]"
          >
            Request Quote <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </button>
        </motion.div>

        <div className="mt-6">
          <WatchButton company={company} />
        </div>
      </main>
    </div>
  );
}
